import { SENDER_TO_RECEIVER_NAMESPACE, RECEIVER_TO_SENDER_NAMESPACE } from '../../constants/namespaces'
import { handleSenderMessage } from './MessageHandler'

// Define global cast type
declare global {
  interface Window {
    cast: any
  }
}

let castReceiverContext: any = null

/**
 * Initializes the Cast Receiver SDK
 */
export function initializeCastReceiver() {
  // Check if the Cast SDK is loaded
  if (!window.cast || !window.cast.framework) {
    console.error('Cast Receiver SDK not loaded')
    return null
  }

  // Get the Cast receiver context
  const context = window.cast.framework.CastReceiverContext.getInstance()
  castReceiverContext = context
  
  // Configure the receiver options
  const options = new window.cast.framework.CastReceiverOptions()
  
  // Configure custom namespaces
  options.customNamespaces = {
    [SENDER_TO_RECEIVER_NAMESPACE]: window.cast.framework.system.MessageType.JSON,
    [RECEIVER_TO_SENDER_NAMESPACE]: window.cast.framework.system.MessageType.JSON
  }
  
  // Disable idle timeout to prevent automatic shutdown
  options.disableIdleTimeout = true
  
  // Skip loading media players since we're displaying text/recipes, not media
  options.skipPlayersLoad = true
  
  // Set up custom messaging for sender namespace
  try {
    // Register for custom messages from sender
    context.addCustomMessageListener(SENDER_TO_RECEIVER_NAMESPACE, handleSenderMessage)
  } catch (error) {
    console.error('Error setting up message listeners:', error)
  }
  
  // Add basic event listeners
  context.addEventListener(
    window.cast.framework.system.EventType.READY, 
    () => {
      // Minimal logging for ready event
      console.log('Cast receiver is ready')
      
      // Create a simple status display in the DOM to show connection state
      try {
        const statusDiv = document.createElement('div');
        statusDiv.id = 'connection-status';
        statusDiv.style.position = 'fixed';
        statusDiv.style.bottom = '10px';
        statusDiv.style.left = '10px';
        statusDiv.style.backgroundColor = 'rgba(0,0,0,0.7)';
        statusDiv.style.color = 'white';
        statusDiv.style.padding = '5px 10px';
        statusDiv.style.borderRadius = '5px';
        statusDiv.style.fontSize = '12px';
        statusDiv.style.fontFamily = 'sans-serif';
        statusDiv.textContent = 'Cast Receiver Ready';
        
        // Only add if there's a body element
        if (document.body) {
          document.body.appendChild(statusDiv);
        } else {
          // If no body yet, wait and try again
          window.addEventListener('DOMContentLoaded', () => {
            document.body.appendChild(statusDiv);
          });
        }
      } catch (e) {
        // Silently ignore status display errors
      }
    }
  )
  
  context.addEventListener(
    window.cast.framework.system.EventType.SENDER_CONNECTED, 
    (event: any) => {
      // Send a confirmation message to the sender
      if (event?.senderId) {
        sendToSender(event.senderId, {
          type: 'RECEIVER_CONNECTED',
          message: 'Receiver connected and ready for messages',
          timestamp: Date.now()
        });
        
        // Update the status display
        try {
          const statusDiv = document.getElementById('connection-status');
          if (statusDiv) {
            statusDiv.textContent = 'Connected to sender: ' + event.senderId.substring(0, 8);
            statusDiv.style.backgroundColor = 'rgba(0,128,0,0.7)';
          }
        } catch (e) {
          // Silently ignore status display errors
        }
      }
    }
  )
  
  context.addEventListener(
    window.cast.framework.system.EventType.SENDER_DISCONNECTED, 
    () => {
      // No logging for disconnect events
    }
  )
  
  // Start the Cast receiver
  context.start(options)
  
  return context
}

/**
 * Sends a message to all connected senders
 */
export function broadcastToSenders(message: any) {
  if (!castReceiverContext) {
    console.error('CastReceiverContext not initialized')
    return
  }
  
  try {
    // Use sendCustomMessage which is available in CAF v3
    castReceiverContext.sendCustomMessage(RECEIVER_TO_SENDER_NAMESPACE, /* senderId= */ undefined, message)
  } catch (error) {
    console.error('Error broadcasting message:', error)
  }
}

/**
 * Sends a message to a specific sender
 */
export function sendToSender(senderId: string, message: any) {
  if (!castReceiverContext) {
    console.error('CastReceiverContext not initialized')
    return
  }
  
  try {
    // Use sendCustomMessage which is available in CAF v3
    castReceiverContext.sendCustomMessage(RECEIVER_TO_SENDER_NAMESPACE, senderId, message)
  } catch (error) {
    console.error('Error sending message to sender:', error)
  }
}

/**
 * Returns the current Cast receiver context
 */
export function getCastReceiverContext() {
  return castReceiverContext
}
